/**
 * BIGWA Core - AppError
 * 
 * Classe base para todos os erros da aplicação.
 * Permite categorização, mensagens amigáveis e recuperabilidade.
 */

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Severidade do erro
 */
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Categoria do erro
 */
export type ErrorCategory = 
  | 'network'      // Erros de rede/conexão
  | 'validation'   // Erros de validação de dados
  | 'auth'         // Erros de autenticação/autorização
  | 'quota'        // Limites excedidos
  | 'notFound'     // Recurso não encontrado
  | 'conflict'     // Conflito de dados
  | 'internal'     // Erro interno inesperado
  | 'external';    // Erro de serviço externo

/**
 * Contexto adicional do erro
 */
export interface ErrorContext {
  [key: string]: unknown;
}

// ============================================================================
// CLASSE BASE
// ============================================================================

/**
 * Classe base para erros da aplicação
 * 
 * @example
 * class CustomError extends AppError {
 *   readonly code = 'CUSTOM_ERROR';
 *   readonly category = 'internal';
 *   readonly severity = 'medium';
 *   readonly userMessage = 'Algo deu errado';
 *   readonly recoverable = true;
 * }
 */
export abstract class AppError extends Error {
  /**
   * Código único do erro (para logging e tracking)
   */
  abstract readonly code: string;

  /**
   * Categoria do erro
   */
  abstract readonly category: ErrorCategory;

  /**
   * Severidade do erro
   */
  abstract readonly severity: ErrorSeverity;

  /**
   * Mensagem amigável para o usuário (pode ser exibida na UI)
   */
  abstract readonly userMessage: string;

  /**
   * Se o erro é recuperável (usuário pode tentar novamente)
   */
  abstract readonly recoverable: boolean;

  /**
   * Contexto adicional para debugging
   */
  readonly context: ErrorContext;

  /**
   * Timestamp de quando o erro ocorreu
   */
  readonly timestamp: string;

  /**
   * Causa original do erro (se houver)
   */
  readonly cause?: Error;

  constructor(
    message: string,
    options?: {
      context?: ErrorContext;
      cause?: Error;
    }
  ) {
    super(message);
    
    // Mantém o nome da classe no stack trace
    this.name = this.constructor.name;
    
    this.context = options?.context ?? {};
    this.cause = options?.cause;
    this.timestamp = new Date().toISOString();

    // Captura stack trace corretamente
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Serializa o erro para logging/reporting
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      category: this.category,
      severity: this.severity,
      message: this.message,
      userMessage: this.userMessage,
      recoverable: this.recoverable,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack,
      cause: this.cause?.message,
    };
  }

  /**
   * Retorna representação para log
   */
  toLogString(): string {
    return `[${this.code}] ${this.message} | Context: ${JSON.stringify(this.context)}`;
  }

  /**
   * Verifica se um erro é instância de AppError
   */
  static isAppError(error: unknown): error is AppError {
    return error instanceof AppError;
  }

  /**
   * Converte erro genérico para AppError
   */
  static from(error: unknown): AppError {
    if (AppError.isAppError(error)) {
      return error;
    }

    if (error instanceof Error) {
      return new UnknownError(error.message, { cause: error });
    }

    return new UnknownError(String(error));
  }
}

// ============================================================================
// ERRO DESCONHECIDO (FALLBACK)
// ============================================================================

/**
 * Erro desconhecido - usado como fallback
 */
export class UnknownError extends AppError {
  readonly code = 'UNKNOWN_ERROR';
  readonly category: ErrorCategory = 'internal';
  readonly severity: ErrorSeverity = 'high';
  readonly userMessage = 'Ocorreu um erro inesperado. Por favor, tente novamente.';
  readonly recoverable = true;

  constructor(message: string, options?: { context?: ErrorContext; cause?: Error }) {
    super(message, options);
  }
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Extrai mensagem de erro de qualquer tipo
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

/**
 * Extrai código de erro se for AppError
 */
export const getErrorCode = (error: unknown): string => {
  if (AppError.isAppError(error)) {
    return error.code;
  }
  return 'UNKNOWN_ERROR';
};

/**
 * Verifica se erro é recuperável
 */
export const isRecoverable = (error: unknown): boolean => {
  if (AppError.isAppError(error)) {
    return error.recoverable;
  }
  return true; // Por padrão, considera recuperável
};

/**
 * Extrai mensagem amigável para o usuário
 */
export const getUserMessage = (error: unknown): string => {
  if (AppError.isAppError(error)) {
    return error.userMessage;
  }
  return 'Ocorreu um erro inesperado. Por favor, tente novamente.';
};
